// What `/v1/streams`' frames MEAN, split from the socket that delivers them.
//
// The socket, the framing and the reconnect ladder are `lib/sse-subscribe.ts` —
// shared with the two append-log views. This half knows only the two event names
// this route emits and the schemas behind them, which is a different reason to
// change from "how hard do we re-dial".

import {
  BlockersTopicRowSchema,
  CiRunWireSchema,
  MachinesTopicRowSchema,
  NotificationWireSchema,
  OverviewApprovalWireSchema,
  OverviewSessionWireSchema,
  PlanSummarySchema,
  QUEUE_STANDING_KEY,
  QueueStandingRowSchema,
  SseEvent,
  type StreamPatch,
  StreamPatchSchema,
  type StreamReady,
  StreamReadySchema,
  TaskWireSchema,
} from '@developerz/domain';
import type { z } from 'zod';
import { FLEET_LIVE_TOPIC, fleetLiveRowSchema } from './fleet-live-frames';
import { parseFrameData, type SseFrame } from './sseFrames';

/** What the account stream tells its view. `bye`/`retry:` are the loop's, not this file's. */
export type AccountStreamSink = {
  /** The negotiated topic set. Always the first frame of a 200. */
  onReady: (ready: StreamReady) => void;
  /** One row upserted or removed — only a row that passed its topic's shape. */
  onPatch: (patch: StreamPatch) => void;
  /**
   * `(topic, key)` moved — EVERY envelope-valid patch, including one whose row
   * this app has no shape for or that failed it. The store must not hold such a
   * row; a REST read keyed on that entity must still re-read, because the change
   * is real whatever the row looks like (`lib/live/change-feed.ts`).
   */
  onChange: (patch: StreamPatch) => void;
};

/**
 * THE PER-TOPIC ROW SHAPE — the half `StreamPatchSchema` deliberately does not
 * know.
 *
 * `StreamRowBodySchema` is `z.record(z.string(), z.unknown())`, and its own
 * docblock says why: "Per-FIELD validation stays the caller's: the shapes are
 * per topic, and this module cannot know which topic a connection asked for."
 * This is that caller. Until it existed, "is an object" was the whole check, and
 * the object went straight into the store for a `createMemo` to dereference.
 *
 * WHAT THAT COST, precisely. Each topic's rows are sorted by a comparator that
 * reads one string field off the row (session recency off `started_iso`,
 * approval age off `opened_iso`, task recency and the bell's merge off
 * `created_at`; the comparators and the merge are since deleted) and calls
 * `.localeCompare` on it. On a row where that field
 * drifted away, `undefined.localeCompare` is a `TypeError` thrown INSIDE the
 * memo. The notification bell's memo is mounted on every page, so one bad patch
 * took `<AppErrorBoundary/>` and the whole shell down, in every open tab at
 * once, on the first frame.
 *
 * THE MAP IS TOTAL over the server's registry, with one topic keyed instead of
 * mapped and one signal-only ({@link SIGNAL_ONLY_TOPICS}). The entity topics
 * (`machines`, `blockers`, `plans`) arrived with the live layer and carry the
 * domain's own topic-row schemas. `streamTopicsFor` (`apps/api/src/routes/streams.ts`) declares six topics:
 * five here, each serialized by the resource named — `OverviewSessionResource`,
 * `OverviewApprovalResource`, `TaskResource` (via `tasksTopicRow`),
 * `CiRunResource`, `NotificationResource` — and `fleet_live` (#6777), whose rows
 * take five shapes by KEY (`fleetLiveRowSchema`, `fleet-live-frames.ts`: an
 * `item:<id>` row or one of four synthetic frames), so a conforming server always
 * parses. A topic absent from this map is dropped rather than stored: a live
 * view this app added without saying what its rows look like would otherwise be
 * the same unvalidated door reopened under a new name, and a loud drop is how
 * its author finds out on the first frame instead of in a comparator.
 *
 * THE COST, stated. A schema is the STRICT shape, and the `sse-frames.ts`
 * header notes the wire contract is one-way so a dashboard can talk to whichever
 * pod a roll hands it. So during a deploy that ADDS a required field, a tab on
 * the new bundle talking to an old pod drops that topic's rows rather than
 * rendering them short. That window is bounded by the roll, it is the same one
 * `lib/ci.ts` and `lib/notifications.ts` already accept on their REST reads
 * (`{ schema: ciRunsSchema }`, `{ schema: notificationsPageSchema }`), and the
 * alternative is the crash above — which is not bounded by anything.
 *
 * A `Map` AND NOT AN OBJECT LITERAL, because `topic` arrives off the wire. A
 * literal inherits `Object.prototype`, so `TOPIC_ROW_SCHEMAS['constructor']`
 * answered the inherited constructor — TRUTHY, so the "no schema, drop it"
 * branch below never fired, and the `.safeParse` after it threw on a function
 * that has none (`toString`, `valueOf`, `hasOwnProperty` the same; `__proto__`
 * differently again, an object rather than a function, and just as truthy).
 * That throw left `readSseBody`, became `{ kind: 'dropped' }` in `attemptSse`,
 * and under the `always` policy re-dialled onto a server that re-sends the same
 * snapshot — so the unknown topic, the one case this map exists to handle
 * safely, was the one that could not heal. A `Map` has no prototype chain to
 * index into, so the class is unrepresentable here rather than guarded against
 * by a `hasOwn` a later edit could drop. Nothing else reads this as an object.
 */
const TOPIC_ROW_SCHEMAS: ReadonlyMap<string, z.ZodType> = new Map<string, z.ZodType>([
  ['approvals', OverviewApprovalWireSchema],
  ['blockers', BlockersTopicRowSchema],
  ['ci', CiRunWireSchema],
  ['machines', MachinesTopicRowSchema],
  ['notifications', NotificationWireSchema],
  ['plans', PlanSummarySchema],
  ['sessions', OverviewSessionWireSchema],
  ['tasks', TaskWireSchema],
]);

/**
 * Topics whose rows are a CHANGE SIGNAL and nothing else: `applyAccountFrames`
 * reports each patch to `onChange` (a live read re-reads on it,
 * `lib/live/live-query.ts`) and the store never holds the row, because no view
 * renders it. `task_changes` is `{ id, status, updated_at }` per task that moves,
 * covering the tasks older than the `tasks` topic's snapshot head
 * (`lib/live/entities.ts`). Dropped WITHOUT the warning an unknown topic gets: it
 * patches on every task write, and a log line per frame over a topic working as
 * designed would bury the warnings that mean something.
 *
 * ITS `deleted` IS NOT A CHANGE. The topic is a 100-row window over the newest
 * `updated_at` (`TaskChangesTopicRowSchema`), so `{deleted: true}` means the task
 * FELL OUT of it because another task moved in — its own row did not change.
 * {@link isChange} therefore drops it: re-reading every open read of an evicted
 * task on every other task's write would be a wasted read per eviction, and a
 * task that DOES move re-enters the window as an upsert, which signals.
 */
const SIGNAL_ONLY_TOPICS: ReadonlySet<string> = new Set(['task_changes']);

/** Whether a parsed patch is a change to its `(topic, key)` — see {@link SIGNAL_ONLY_TOPICS}. */
function isChange(patch: StreamPatch): boolean {
  return !('deleted' in patch && SIGNAL_ONLY_TOPICS.has(patch.topic));
}

/**
 * Validate an upsert's row against its topic's shape. A `deleted` patch carries
 * no row and is passed through — it only ever removes a key.
 *
 * The `tasks` topic carries ONE synthetic row that is not a task: the
 * account-wide queue depth under {@link QUEUE_STANDING_KEY} (`{ depth }`, not a
 * `TaskWire`). It is keyed, not topic-ed, so the key is checked before the
 * topic — made at the door rather than trusted to every consumer. The
 * `fleet_live` topic goes by key for the same reason, before the topic map.
 *
 * Exported for its own unit test: this is a pure verdict over a parsed patch,
 * and the store is not needed to state it.
 */
export function acceptPatch(patch: StreamPatch): StreamPatch | null {
  if (SIGNAL_ONLY_TOPICS.has(patch.topic)) return null;
  if ('deleted' in patch) return patch;
  if (patch.topic === FLEET_LIVE_TOPIC) {
    const keyed = fleetLiveRowSchema(patch.key);
    if (!keyed) {
      console.warn(`[sse] dropped fleet_live patch: no row schema for key "${patch.key}"`);
      return null;
    }
    return checkRow(patch, keyed);
  }
  const schema =
    patch.topic === 'tasks' && patch.key === QUEUE_STANDING_KEY
      ? QueueStandingRowSchema
      : TOPIC_ROW_SCHEMAS.get(patch.topic);
  if (!schema) {
    console.warn(`[sse] dropped patch: no row schema for topic "${patch.topic}"`);
    return null;
  }
  return checkRow(patch, schema);
}

function checkRow(
  patch: Extract<StreamPatch, { row: unknown }>,
  schema: z.ZodType,
): StreamPatch | null {
  const parsed = schema.safeParse(patch.row);
  if (!parsed.success) {
    console.warn(`[sse] dropped ${patch.topic} row: schema mismatch`, parsed.error.issues);
    return null;
  }
  return patch;
}

/**
 * Dispatch one network chunk's worth of frames, in order.
 *
 * A batch rather than a frame: `sseSubscribe` hands over everything one
 * `reader.read()` carried, so a snapshot of forty rows costs forty `produce`
 * calls on the store and not forty renders of every list watching it. Each frame
 * is Zod-validated before it touches the store — the envelope by
 * {@link StreamPatchSchema}, then the row by its topic's shape
 * ({@link acceptPatch}) — and a malformed one is dropped and logged, never
 * thrown, so one bad row cannot tear the connection down.
 */
export function applyAccountFrames(frames: readonly SseFrame[], sink: AccountStreamSink): void {
  for (const frame of frames) {
    if (frame.event === SseEvent.Ready) {
      const ready = parseFrameData(StreamReadySchema, frame.data);
      if (ready) sink.onReady(ready);
    } else if (frame.event === SseEvent.Patch) {
      const patch = parseFrameData(StreamPatchSchema, frame.data);
      if (patch && isChange(patch)) sink.onChange(patch);
      const accepted = patch && acceptPatch(patch);
      if (accepted) sink.onPatch(accepted);
    }
  }
}
